/**
 * Idiomas de la CMDB.
 *
 * `BUILTIN_LANGUAGES` son los idiomas que viajan en el build (JSON en
 * `src/i18n`). A partir de ahí, el módulo de Idiomas puede añadir más: el
 * backend los devuelve en `/api/i18n/languages` y su catálogo se carga en
 * caliente con `i18n.addResourceBundle`.
 *
 * Este módulo NO importa React ni i18next a propósito: lo usan la configuración
 * de i18n, el onboarding, el perfil y el panel de plataforma.
 */

export const LANGUAGE_STORAGE_KEY = 'cmdb-language';
/** Clave histórica: se sigue leyendo para no perder la elección al actualizar. */
export const LEGACY_LANGUAGE_STORAGE_KEY = 'language';

export const BUILTIN_LANGUAGES = [
  {
    code: 'es',
    name: 'Castellano',
    nativeName: 'Castellano',
    nameEs: 'Castellano',
    nameEn: 'Spanish',
    bcp47: 'es-ES',
    builtin: true,
    sortOrder: 10,
  },
  {
    code: 'ca',
    name: 'Català',
    nativeName: 'Català',
    nameEs: 'Catalán',
    nameEn: 'Catalan',
    bcp47: 'ca-ES',
    builtin: true,
    sortOrder: 20,
  },
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    nameEs: 'Inglés',
    nameEn: 'English',
    bcp47: 'en-GB',
    builtin: true,
    sortOrder: 30,
  },
];

const BUILTIN_BY_CODE = new Map(BUILTIN_LANGUAGES.map((lang) => [lang.code, lang]));

/** Código normalizado (minúsculas; `ca-ES` → `ca`, `pt-BR` → `pt-br`). */
export function normalizeLanguageCode(value) {
  if (!value || typeof value !== 'string') return null;
  const lower = value.trim().toLowerCase().replace('_', '-');
  if (!/^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/.test(lower)) return null;
  // Los catálogos del build usan el código base; para idiomas nuevos se
  // respeta la variante regional (`pt-br`).
  const base = lower.split('-')[0];
  return BUILTIN_BY_CODE.has(base) ? base : lower;
}

export function isBuiltinLanguage(code) {
  const normalized = normalizeLanguageCode(code);
  return normalized ? BUILTIN_BY_CODE.has(normalized) : false;
}

export function languageMeta(code) {
  const normalized = normalizeLanguageCode(code);
  return (
    BUILTIN_BY_CODE.get(normalized) || {
      code: normalized || 'es',
      name: normalized || 'es',
      nativeName: normalized || 'es',
      nameEs: normalized || 'es',
      nameEn: normalized || 'es',
      bcp47: normalized || 'es',
      builtin: false,
    }
  );
}

/** Etiqueta corta para chips: `ES`, `CA`, `EN`, `PT-BR`… */
export function languageBadge(code) {
  return String(normalizeLanguageCode(code) || 'es').toUpperCase();
}

/** Idioma del navegador que mejor encaja con los disponibles. */
export function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return 'es';

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean);

  for (const raw of candidates) {
    const code = String(raw).toLowerCase();
    if (code.startsWith('ca')) return 'ca';
    if (code.startsWith('es')) return 'es';
    if (code.startsWith('en')) return 'en';
  }
  return 'es';
}

export function readStoredLanguage() {
  try {
    const stored =
      localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
      localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY);
    return normalizeLanguageCode(stored);
  } catch {
    return null;
  }
}

/**
 * Idioma con el que arranca la app ANTES de preguntar nada:
 * elección guardada en este navegador → idioma del navegador → español.
 */
export function initialLanguage() {
  return readStoredLanguage() || detectBrowserLanguage();
}

export function rememberLanguage(code) {
  const normalized = normalizeLanguageCode(code) || 'es';
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    // Se mantiene la clave histórica: hay versiones antiguas que la leen.
    localStorage.setItem(LEGACY_LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // Navegador con almacenamiento bloqueado: la app funciona igual.
  }
  return normalized;
}

function remoteToMeta(language) {
  const code = normalizeLanguageCode(language?.code);
  if (!code) return null;
  return {
    code,
    name: language.name || code,
    nativeName: language.name || code,
    nameEs: language.name_es || language.name || code,
    nameEn: language.name || code,
    bcp47: code,
    builtin: isBuiltinLanguage(code),
    sortOrder: Number(language.sort_order ?? 100),
    enabled: language.enabled !== false,
  };
}

/**
 * Funde los idiomas del build con los que devuelve el servidor.
 * Los del build siempre están (aunque el backend aún no esté migrado); los
 * remotos se añaden sin duplicar y ordenados por `sort_order`.
 */
export function mergeLanguages(remoteLanguages = []) {
  const merged = new Map(BUILTIN_LANGUAGES.map((lang) => [lang.code, lang]));
  for (const remote of remoteLanguages || []) {
    const meta = remoteToMeta(remote);
    if (!meta) continue;
    merged.set(meta.code, { ...(merged.get(meta.code) || {}), ...meta });
  }
  return Array.from(merged.values()).sort(
    (a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100) || a.code.localeCompare(b.code)
  );
}
